import { useCallback, useState } from "react";
import type { APIRecord } from "server/services/tables/types/records";
import type { APIFieldLatest } from "server/services/tables/types/tables-latest";
import { cn } from "../../utils/cn";
import { trpc } from "../../utils/trpc";
import { InteractButton } from "./InteractButton";
import { toast } from "@/components/Toaster/Toaster";
import { useInterfacesTheme } from "lib/theme/ThemeProvider";
import { Button } from "@/block-system/brickz/components/ui/Button";
import { zrpc } from "@/lib/zrpc";
import { useQueryClient } from "@tanstack/react-query";
import { useInvalidateBlocksUsingTable } from "@/block-system/blocks/Table/lib/invalidateTableRecordQueries";

interface ContinueZapButtonData {
  callback_urls: string[];
  last_click_label: string | null;
}

export function ContinueZapButton({
  field,
  record,
  tableId,
  blockId,
}: {
  field: Pick<APIFieldLatest, "id" | "options">;
  record: Pick<APIRecord, "id" | "data">;
  tableId: string;
  blockId: string | undefined;
}) {
  const recordData = record.data[`f${field.id}`] as any;
  const buttonRecordData: ContinueZapButtonData | null = recordData;

  const [pickedButton, setPickedButton] = useState<string | null>(
    buttonRecordData?.last_click_label ?? null
  );
  const interfacesTheme = useInterfacesTheme();

  const isDeliverChangesStep = buttonRecordData == null;
  if (isDeliverChangesStep) {
    return (
      <DeliverRecordChangesButton
        fieldId={field.id}
        tableId={tableId}
        recordId={record.id}
        blockId={blockId}
      />
    );
  }

  const buttonLabels = getButtonLabels({ fieldOptions: field.options });

  const interactionFinished = pickedButton != null;
  return (
    <div
      className={cn("flex", "gap-2", {
        "opacity-50": interactionFinished,
      })}
    >
      {buttonLabels.map((label, index) => {
        /**
         * To align more closely with Tables UI.
         * The "picked" option is disabled, but has appearance of a "primary" button.
         */
        const isPickedButton = pickedButton === label;

        const testId = `continue-zap-button-${field.id}-${
          isPickedButton ? "picked" : "not-picked"
        }`;

        return (
          <InteractButton
            onClick={() => {
              setPickedButton(label);
            }}
            data-testid={testId}
            tableId={tableId}
            fieldId={field.id}
            recordId={record.id}
            key={index}
            disableOnClick={true}
            isDisabled={interactionFinished}
            isEnabled={!interactionFinished}
            className={cn({
              "disabled:border-zi-primary disabled:bg-zi-primary disabled:text-zi-white":
                isPickedButton && !interfacesTheme,
              "disabled:border-primary disabled:bg-primary disabled:text-primary-foreground":
                isPickedButton && interfacesTheme,
            })}
          >
            {label}
          </InteractButton>
        );
      })}
    </div>
  );
}

function getButtonLabels({
  fieldOptions,
}: {
  fieldOptions: APIFieldLatest["options"];
}) {
  const firstButtonLabel =
    fieldOptions?.button?.label ?? fieldOptions?.button?.custom_label ?? "";

  const additionalLabels = fieldOptions?.button?.additional_labels ?? [];

  const buttonLabels = [firstButtonLabel, ...additionalLabels];
  return buttonLabels;
}

function DeliverRecordChangesButton({
  tableId,
  recordId,
  fieldId,
  blockId,
}: {
  tableId: string;
  recordId: string;
  fieldId: number;
  blockId: string | undefined;
}) {
  const propagateChanges = useWaitForDeliverChanges();

  const { isPending: isConfirming, mutate: deliverRecordChanges } =
    trpc.tableRecords.deliverRecordChanges.useMutation({
      onSuccess: async function () {
        if (!blockId) {
          return;
        }

        toast.success({ message: "Interaction triggered successfully." });
        await propagateChanges({ tableId, recordId, fieldId, blockId });
      },
      onError: async function () {
        toast.error({
          message: "Failed to trigger interaction. Please try again.",
        });
      },
      meta: {
        noToast: true,
      },
    });

  return (
    <Button
      type="button"
      size="small"
      variant="outline"
      disabled={isConfirming}
      isLoading={isConfirming}
      onClick={() => {
        deliverRecordChanges({
          tableId,
          recordId,
        });
      }}
    >
      Send to Zap
    </Button>
  );
}

/**
 * After "delivering changes", we have to poll the record to ensure we can proceed to the next step.
 * When changes are "in flight", the returned tables, record, field data are the same as if we did not make any changes.
 */
function useWaitForDeliverChanges() {
  const queryClient = useQueryClient();
  const invalidateBlocksUsingTable = useInvalidateBlocksUsingTable();

  return useCallback(
    async ({
      tableId,
      recordId,
      fieldId,
      blockId,
    }: {
      tableId: string;
      recordId: string;
      fieldId: number;
      blockId: string;
    }) => {
      let isStateSettled = false;

      do {
        await new Promise((resolve) => setTimeout(resolve, 3_000));

        const response = await queryClient.fetchQuery(
          zrpc.interfaces.queryOptions(
            "get",
            "/api/interfaces/v0/blocks/table/{block_id}/tables/{table_id}/records/{record_id}",
            {
              params: {
                path: {
                  block_id: blockId,
                  table_id: tableId,
                  record_id: recordId,
                },
              },
            }
          )
        );

        const fieldData = response.results.data.data[`f${fieldId}`];
        if (fieldData != null) {
          isStateSettled = true;
        }
      } while (isStateSettled === false);

      /**
       * Now that we know the changes to the record have been propagated, we can invalidate the cache.
       */

      void invalidateBlocksUsingTable(tableId);
    },
    [invalidateBlocksUsingTable, queryClient]
  );
}
